import React from 'react';
import { Badge } from '../../components/Badge/Badge';
import type { BadgeVariant } from '../../components/Badge/Badge';
import type { PageHeaderBadgeVariant, PageHeaderProps } from './PageHeader.types';

const variantMap: Record<PageHeaderBadgeVariant, BadgeVariant> = {
  success: 'green',
  error: 'red',
  warning: 'amber',
  info: 'blue',
};

const ChevronLeftIcon: React.FC = () => (
  <svg
    className="h-5 w-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

/**
 * PageHeader - Encabezado de página reutilizable y responsive.
 *
 * @description
 * Altura uniforme: sin breadcrumb, todos los headers miden lo mismo tengan o
 * no badge o botones.
 * - Celular (< 768 px): 48 px. Sin breadcrumb; botón "Volver" si hay
 *   `onBack`; título h4; acciones = `mobileActions` o, si no hay, `actions`.
 * - Tablet y laptop (768–1535 px): 48 px sin breadcrumb. Título h4 y badge
 *   de 22 px.
 * - Pantalla grande (≥ 1536 px): 60 px sin breadcrumb. Título h3 y badge
 *   de 24 px.
 *
 * Regla: los botones de `actions` y `mobileActions` deben medir como máximo
 * 32 px de alto (por ejemplo `size="sm"`). Un botón más alto vuelve a estirar
 * el header y rompe la altura uniforme.
 *
 * Si `title` es texto, se renderiza en un `<h1>` que se corta con "…".
 * Si es un elemento React, se muestra tal cual (debe traer su propio `<h1>`).
 *
 * @example
 * <PageHeader title="Comprobantes Electrónicos" />
 *
 * @example
 * <PageHeader
 *   breadcrumb={<Breadcrumb items={[...]} />}
 *   onBack={() => navigate(-1)}
 *   title="Nueva Emisión"
 *   badge={{ text: 'Borrador', variant: 'warning' }}
 *   actions={<Button iconOnly icon={<Settings />} />}
 *   mobileActions={<OverflowMenu items={[...]} />}
 * />
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  breadcrumb,
  badge,
  titleAddon,
  actions,
  mobileActions,
  onBack,
  backLabel = 'Volver',
  className = '',
}) => {
  const resolvedMobileActions = mobileActions ?? actions;

  const rootClassName = [
    'sticky top-0 z-10 shrink-0',
    'flex items-center justify-between gap-2 md:gap-4',
    'bg-surface-0 border-b border-[color:var(--border-default)]',
    // Altura mínima uniforme: 48 px (celular a laptop) y 60 px (≥ 1536 px)
    'min-h-page-header 2xl:min-h-page-header-lg',
    // Espaciado: el mínimo necesario para que botones de 32 px quepan sin
    // estirar el header; la altura mínima los centra.
    'px-4 py-1.5 md:px-6 2xl:py-3',
    breadcrumb ? 'md:py-2.5' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Sin breadcrumb las acciones se centran; con breadcrumb, entre tablet y
  // laptop se alinean con la línea del título.
  const actionsAlign = breadcrumb ? 'self-end 2xl:self-center' : 'self-center';

  return (
    <div className={rootClassName}>
      {/* Área izquierda: Volver (celular) + Breadcrumb (tablet+) + Título */}
      <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-col md:items-stretch md:gap-1 2xl:gap-1.5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel}
            title={backLabel}
            className="-ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary hover:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 md:hidden"
          >
            <ChevronLeftIcon />
          </button>
        )}

        {breadcrumb && (
          <div className="hidden min-w-0 text-sm md:block">{breadcrumb}</div>
        )}

        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 md:flex-nowrap">
          {typeof title === 'string' ? (
            <h1 className="min-w-0 max-w-full truncate font-display text-h4 text-primary 2xl:text-h3">
              {title}
            </h1>
          ) : (
            title
          )}

          {titleAddon && <span className="shrink-0">{titleAddon}</span>}

          {badge && (
            <span className="inline-flex shrink-0">
              <Badge
                variant={variantMap[badge.variant ?? 'info']}
                iconLeft={badge.icon}
                size="auto"
                shape="rounded"
                noDot
              >
                {badge.text}
              </Badge>
            </span>
          )}
        </div>
      </div>

      {/* Acciones: celular */}
      {resolvedMobileActions && (
        <div className="flex shrink-0 items-center gap-1 md:hidden">
          {resolvedMobileActions}
        </div>
      )}

      {/* Acciones: tablet en adelante */}
      {actions && (
        <div className={`hidden shrink-0 items-center gap-2 md:flex ${actionsAlign}`}>
          {actions}
        </div>
      )}
    </div>
  );
};
